/**
 * The library, as the browse page needs it.
 *
 * DERIVED AT BUILD TIME, AND IT USED NOT TO BE. This file imported `games.js` and
 * derived the list in the browser, on the argument that a derived list cannot
 * fall behind the thing it is derived from and a generator can. That argument is
 * still right, and it is why `row.js` exists: the derivation is one function, run
 * by the build over the library and by `addGames` below over a visitor's own PGN,
 * so there is still exactly one definition of what a row is.
 *
 * What changed is the price. `games.js` is 802KB — 446KB of it the per-half-move
 * narration the 3D player reads — and every visitor who opened the front page
 * downloaded all of it to draw a table of eleven columns that touch none of it.
 * 113KB over the wire against 7KB for the same rows, on the one page every link
 * to this site lands on.
 *
 * So `scripts/build-site.mjs` runs the derivation and writes
 * `library.generated.js` beside this file — the same arrangement as
 * `worker/game-ids.generated.js`, gitignored and rewritten by every build and by
 * `server.js` at boot, so there is nothing to remember to run.
 */

import { ROWS, SETS } from './library.generated.js';
import { HOUSE, rowFor } from './row.js';

export { HOUSE };

/**
 * MUTATED IN PLACE by `addGames` and `dropGames`, so this is the array the
 * generated file handed over rather than a copy of it — every page holds a
 * reference taken at import time, and rebinding would leave them pointing at the
 * old list. See `addGames`.
 */
export const INDEX = ROWS;

export const BY_ID = new Map(INDEX.map((row) => [row.id, row]));

/**
 * Add games that were not in `games.js` — a visitor's own PGN, and nothing else.
 *
 * MUTATES `INDEX` AND `BY_ID` RATHER THAN REBUILDING THEM, which is the only
 * thing that can work: every page that reads the library holds a reference to
 * these two, taken at import time, and rebinding the exports would leave those
 * references pointing at the old list. So the arrays stay the same objects and
 * grow. Called once, from `imported.js`, before the page derives anything from
 * `INDEX` — see the note on `load()` there for why the order matters.
 */
export function addGames(games) {
  const rows = [];
  for (const game of games) {
    const row = rowFor(game, SETS);
    // `BY_ID` rather than a scan of `INDEX`, because this is called once with
    // every game of a file: a `findIndex` per game over a list that grows by one
    // each time is quadratic, and five thousand games is twelve million string
    // comparisons for an answer a map already has. The O(n) `indexOf` is left on
    // the REPLACE path, which only happens when the same id is registered twice.
    const had = BY_ID.get(row.id);
    if (had) INDEX[INDEX.indexOf(had)] = row; else INDEX.push(row);
    BY_ID.set(row.id, row);
    rows.push(row);
  }
  return rows;
}

/** And the other direction, for a file the visitor has just removed. */
export function dropGames(matches) {
  for (let i = INDEX.length - 1; i >= 0; i--) {
    if (!matches(INDEX[i].id)) continue;
    BY_ID.delete(INDEX[i].id);
    INDEX.splice(i, 1);
  }
}

/**
 * Every set the scene can be played on, in the order it declares them.
 *
 * The `board` parameter is a menu of eleven options — the game's own, these ten,
 * and a custom one — and their names ("rosewood & cream") are a thing you have to
 * imagine. The watch page draws them instead; this is where it gets the colours.
 */
export const BOARDS = Object.entries(SETS).map(([id, set]) => ({
  id, label: set.label, light: set.light, dark: set.dark,
}));

/** Every era in the library, in the order they were played. */
export function eras() {
  return [...new Set(INDEX.slice().sort((a, b) => a.year - b.year).map((r) => r.era))];
}

/** Where a game's watch page is. Deep links are the player's business; see it. */
export function watchUrl(id, move) {
  const query = new URLSearchParams({ game: id });
  if (move) query.set('move', String(move));
  return `/watch/player.html?${query}`;
}
